import { useCallback, useEffect, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import contractPicklistsService from '../../../services/compliance/contractPicklists.service';
import { ContractPicklistItem, ContractPicklistKind } from '../../../types/compliance';

interface Props {
  kind: ContractPicklistKind;
  /** Section heading, e.g. "Departments". */
  title: string;
  /** Lowercase singular for messages, e.g. "department". */
  noun: string;
  /** Compliance managers edit; everyone else sees the list read-only. */
  canEdit: boolean;
}

const errorDetail = (error: any): string =>
  error?.response?.data?.detail || error?.message || 'Something went wrong.';

const usageLabel = (n: number) => (n === 0 ? 'Not used' : `${n} contract${n === 1 ? '' : 's'}`);

const inputClass =
  'border rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600';
const iconButtonClass =
  'p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none';

/**
 * One editable dropdown list (Departments or Categories) on Compliance →
 * Settings: add, rename, move up/down, delete. Contracts store the name as
 * text, so the backend renames it on every contract too, and refuses a delete
 * while any contract uses it; the usage count here tells the user up front.
 */
export default function PicklistEditor({ kind, title, noun, canEdit }: Props) {
  const [items, setItems] = useState<ContractPicklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await contractPicklistsService.list(kind));
    } catch (e) {
      setError(errorDetail(e));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  // Runs one edit; on failure shows the server's reason and reloads the list
  // so the screen matches what's actually saved.
  const run = async (edit: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await edit();
    } catch (e) {
      setError(errorDetail(e));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const name = newName.trim();
    if (!name) return;
    return run(async () => {
      const created = await contractPicklistsService.create(kind, name);
      setItems((prev) => [...prev, created]);
      setNewName('');
    });
  };

  const saveRename = () => {
    if (!editing) return;
    const item = items.find((i) => i.id === editing.id);
    const name = editing.name.trim();
    if (!item || !name || name === item.name) {
      setEditing(null);
      return;
    }
    if (
      item.contract_count > 0 &&
      !window.confirm(
        `Rename "${item.name}" to "${name}"? This also renames it on ${usageLabel(item.contract_count)}.`,
      )
    ) {
      return;
    }
    return run(async () => {
      const updated = await contractPicklistsService.rename(kind, item.id, name);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, name: updated.name } : i)));
      setEditing(null);
    });
  };

  const move = (index: number, delta: -1 | 1) => {
    const next = [...items];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    setItems(next);
    return run(() => contractPicklistsService.reorder(kind, next.map((i) => i.id)));
  };

  const remove = (item: ContractPicklistItem) => {
    if (!window.confirm(`Delete the ${noun} "${item.name}"?`)) return;
    return run(async () => {
      await contractPicklistsService.remove(kind, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    });
  };

  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel">
      <h2 className="text-lg font-semibold text-body">{title}</h2>
      <p className="text-xs text-muted mb-3">
        {canEdit
          ? `Renaming updates every contract that uses the ${noun}. A ${noun} can only be deleted once no contract uses it.`
          : `Only compliance managers can change this list.`}
      </p>

      {error && (
        <div role="alert" className="mb-3 text-sm rounded border border-red-300 bg-red-50 text-red-800 px-3 py-2 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {items.map((item, index) => (
            <li key={item.id} className="flex items-center gap-2 py-1.5">
              {editing?.id === item.id ? (
                <form
                  className="flex flex-1 items-center gap-2"
                  onSubmit={(e) => { e.preventDefault(); saveRename(); }}
                >
                  <input
                    autoFocus
                    value={editing.name}
                    maxLength={100}
                    onChange={(e) => setEditing({ id: item.id, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }}
                    className={`${inputClass} flex-1`}
                  />
                  <button type="submit" disabled={busy} className="px-2 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                    Save
                  </button>
                  <button type="button" onClick={() => setEditing(null)} className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600">
                    Cancel
                  </button>
                </form>
              ) : (
                <>
                  <span data-name className="flex-1 text-sm text-body">{item.name}</span>
                  <span className="text-xs text-muted w-24 text-right">{usageLabel(item.contract_count)}</span>
                  {canEdit && (
                    <div className="flex items-center">
                      <button type="button" aria-label={`Move ${item.name} up`} title="Move up" disabled={busy || index === 0} onClick={() => move(index, -1)} className={iconButtonClass}>
                        <ChevronUpIcon className="w-4 h-4" />
                      </button>
                      <button type="button" aria-label={`Move ${item.name} down`} title="Move down" disabled={busy || index === items.length - 1} onClick={() => move(index, 1)} className={iconButtonClass}>
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                      <button type="button" aria-label={`Rename ${item.name}`} title="Rename" disabled={busy} onClick={() => setEditing({ id: item.id, name: item.name })} className={iconButtonClass}>
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete ${item.name}`}
                        title={item.contract_count > 0 ? `In use by ${usageLabel(item.contract_count)}; move them to another ${noun} first` : 'Delete'}
                        disabled={busy || item.contract_count > 0}
                        onClick={() => remove(item)}
                        className={`${iconButtonClass} hover:text-red-600 dark:hover:text-red-400`}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && !loading && (
        <form
          className="mt-3 flex items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); add(); }}
        >
          <input
            value={newName}
            maxLength={100}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`New ${noun}`}
            className={`${inputClass} flex-1`}
          />
          <button type="submit" disabled={busy || !newName.trim()} className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            Add
          </button>
        </form>
      )}
    </section>
  );
}
